import type { TFunction } from 'i18next'
import type { User } from '@/types'
import {
  displayErrorMessage,
  getErrorMessage,
  getListItemValidationErrors,
  getValidationErrors,
} from '@/utils/error'
import { formatUserFullName, getUserDisplayName } from '@/utils/userName'

/**
 * Подпись ученика в выборе участников группы: «Фамилия Имя (почта)».
 *
 * У ученика без кабинета почты нет — тогда только имя или отображаемое имя.
 */
export function getMemberLabel(user: User): string {
  const name = formatUserFullName(user)
  const email = user.email?.trim() || ''

  if (name && email) {
    return `${name} (${email})`
  }

  return getUserDisplayName(user, { prefer: 'full_name' })
}

/**
 * Понятный текст ошибки добавления участников в группу.
 *
 * В группу попадают только свои ученики с активным договором: чужого
 * пользователя API отклоняет ответом 422 с ключом `<field>.N` на каждого
 * такого участника и не добавляет никого из списка. Тогда называем
 * отклонённых поимённо — иначе учитель не поймёт, кого убрать. Остальные
 * ошибки — сообщение сервера или `fallbackKey`.
 */
export function describeMembersError(
  error: unknown,
  field: string,
  submitted: User[],
  t: TFunction,
  fallbackKey: string,
): string {
  const errors = getValidationErrors(error)
  const rejected = getListItemValidationErrors(errors, field)

  if (rejected) {
    const names = rejected.indexes
      .map((index) => submitted[index])
      .filter((user): user is User => user !== undefined)
      .map(getMemberLabel)

    if (names.length > 0) {
      return t('groups.membersRejected', {
        reason: rejected.message.replace(/[.\s]+$/, ''),
        names: names.join(', '),
      })
    }
  }

  // Одиночный участник (`student_id`) или ошибка поля без индекса
  const fieldMessage = errors?.[field]?.[0]
  if (fieldMessage) {
    return fieldMessage
  }

  return displayErrorMessage(getErrorMessage(error), t(fallbackKey))
}
